import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CUSTOMER_EXECUTION_REPORT,
  CustomerExecutionReport,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  DEFAULT_MAX_ROWS,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  WSBlotterTableMaxRecordsReachedWarning,
  filterByColumnMainMenuItems,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useCustomerExecutionReportColumns,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type Column,
} from '@talos/kyoko';
import { useMemo } from 'react';

import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import type { GetMainMenuItemsParams, RowDoubleClickedEvent } from 'ag-grid-community';
import {
  colIDToFilterBuilderKey,
  onlyServerFilterKeys,
  useCustomerExecutionReportsFilter,
  type CustomerExecutionReportsTableFilter,
} from './useCustomerExecutionReportsFilter';
import { useCustomerExecutionReportsMenu } from './useCustomerExecutionReportsMenu';

export interface FilteredCustomerExecutionReportsParams {
  blotterID: string;
  tabLabel?: string;
  defaultColumns?: Column[];
  defaultFilter?: CustomerExecutionReportsTableFilter;
  initialIsOpen?: boolean;
  onCloneTab?: () => void;
  onRowDoubleClicked?: (event: RowDoubleClickedEvent<CustomerExecutionReport>) => void;
}

export function CustomerExecutionReportsBlotterTable({
  blotterID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  initialIsOpen,
  onCloneTab,
  onRowDoubleClicked,
}: FilteredCustomerExecutionReportsParams) {
  const { getConfig } = useOrgConfiguration();
  const mixpanel = useMixpanel();
  const columns = useCustomerExecutionReportColumns({ defaultColumns });

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const filterResults = useCustomerExecutionReportsFilter({
    persistedBlotterTable,
  });
  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const customerExecutionReportsMenu = useCustomerExecutionReportsMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
  });

  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey,
      openClause: filterBuilderAccordion.openClause,
      mixpanel,
    });
  });

  const columnsWithMenu = useMemo(
    () => [...columns, ...customerExecutionReportsMenu.columns],
    [columns, customerExecutionReportsMenu.columns]
  );

  const request = useMemo(
    () => ({
      tag: blotterID,
      name: CUSTOMER_EXECUTION_REPORT,
      sort_by: '-Timestamp',
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [blotterID, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable({
    request,
    rowID: CustomerExecutionReport.rowID,
    clientLocalFilter,
    startingRowLimit: getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS),
    persistence: persistedBlotterTable,
    columns: columnsWithMenu,
    getExtraMainMenuItems,
    contextMenuOptions: {
      getCustomContextMenuItems: customerExecutionReportsMenu.getCustomContextMenuItems,
    },
    gridOptions: {
      onRowDoubleClicked,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
    },
    exportOptions: {
      name: 'CustomerExecutionReports',
      tabLabel: tabLabel,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />

            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.Timestamp} />
      <BlotterTable {...blotterTable} />
      {customerExecutionReportsMenu.dialogs}
    </>
  );
}
